import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import classNames from 'classnames/bind';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckSquare } from '@fortawesome/pro-solid-svg-icons/faCheckSquare';
import { faSquare } from '@fortawesome/pro-regular-svg-icons/faSquare';
import moment from 'moment';

import { UK_INSIGHTS } from '../../../../data/features';
import InsiderSvc from '../../../../services/dbServices/InsiderSvc';

import Tooltip from '../../../Tooltip';
import Scrollable from '../../../Shared/Scrollable';
import Preloader from '../../../Preloader';
import EquityItem from './EquityItem';
import EquityFundraisersMobile from './EquityFundraisersMobile';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

const EquityFundraisers = (props) => {
  const { } = props;
  let mounted = true;
  const [activeType, setActiveType] = useState('count');
  const [activeTime, setActiveTime] = useState('monthly');
  const [showPeriodChange, setShowPeriodChange] = useState(false);
  const [currentData, setCurrentData] = useState({
    loading: true,
    newLoading: false,
    data: [],
  });

  const hasInsightsAccess = useSelector((state) => (
    (state.subscriptions.permissions.features || []).includes(UK_INSIGHTS)
  ));
  const width = useSelector(({ common }) => (common.width));

  const generateData = (data) => {
    const newData = [];
    for (let i = 0; i < data.length; i += 1) {
      const currentObj = data[i];
      const currentDate = moment(currentObj.date, 'YYYY-MM-DD');

      const oldDate = moment(currentObj.date, 'YYYY-MM-DD').subtract(1, 'months');
      const oldMonth = moment(oldDate).format('YYYY-MM');
      const findOldMonth = data.find((f) => f.date.includes(oldMonth));

      const oldYear = moment(moment(currentObj.date, 'YYYY-MM-DD').subtract(1, 'years')).format('YYYY-MM');
      const findOldYear = data.find((f) => f.date.includes(oldYear));

      const currentDateDays = currentDate.daysInMonth();
      const oldDateDays = oldDate.daysInMonth();

      newData.push({
        currentMonth: currentDate.format('MMM'),
        currentMonthNumber: currentDate.format('MM'),
        currentYear: currentDate.format('YYYY'),
        momChanges: findOldMonth
          ? (
            (
              ((currentObj.fundsRaised / currentDateDays)
                / (findOldMonth.fundsRaised / oldDateDays))
              - 1)
            * 100)
          : '',
        yoyChanges: findOldYear ? (((currentObj.fundsRaised / findOldYear.fundsRaised) - 1) * 100) : '',
        momChangesCount: findOldMonth
          ? (
            (
              ((currentObj.transactionCount / currentDateDays)
                / (findOldMonth.transactionCount / oldDateDays))
              - 1)
            * 100)
          : '',
        yoyChangesCount: findOldYear ? (((currentObj.transactionCount / findOldYear.transactionCount) - 1) * 100) : '',
        ...currentObj,
      });
    }
    return newData;
  };

  const callAPI = () => {
    const params = {
      time_period: 'all',
      aggregate_interval: 'monthly',
    };
    setCurrentData({
      ...currentData,
      newLoading: !currentData.loading,
    });

    InsiderSvc.getCompanyInsiderFundraisingGraphs(params)
      .then((data) => {
        if (!mounted) {
          return;
        }

        const newData = generateData(data);
        setCurrentData({
          loading: false,
          newLoading: false,
          data: newData.slice(-12),
        });
      });
  };

  useEffect(() => {
    callAPI();
    return () => {
      mounted = false;
    };
  }, []);

  const isCommingSoon = activeTime === 'quarterly' || activeTime === 'annual';

  return (
    <div className={Styles.fundraisers}>
      <div className={Styles['fundraisers-header']}>
        <div className={Styles['fundraisers-header-count']}>
          <div className={Styles['fundraisers-header-types']}>
            <div
              className={cx('fundraisers-header-type', { active: activeType === 'value' })}
              onClick={() => setActiveType('value')}
            >
              Value
            </div>
            <div
              className={cx('fundraisers-header-type', { active: activeType === 'count' })}
              onClick={() => setActiveType('count')}
            >
              Count
            </div>
          </div>
          <Tooltip
            newIcon
            hasBigScreenDesign
            isCentered
            dynamicPosition
            contentStyle={{ left: 0 }}
            className={Styles['fundraisers-header-tooltip']}
          >
            Value shows the aggregate value of all reported investments.
            Count shows the count of reported investments.
          </Tooltip>
        </div>
        <div className={Styles['fundraisers-header-count']}>
          <div className={cx('fundraisers-header-types', 'time')}>
            <div
              className={cx('fundraisers-header-type', { active: activeTime === 'monthly' })}
              onClick={() => setActiveTime('monthly')}
            >
              Monthly
            </div>
            <Tooltip
              hasBigScreenDesign
              isCentered
              className={Styles['fundraisers-header-type-tooltip']}
              icon={(
                <div
                  className={cx('fundraisers-header-type', { active: activeTime === 'quarterly' })}
                >
                  Quarterly
                </div>
              )}
            >
              Coming Soon
            </Tooltip>
            <Tooltip
              hasBigScreenDesign
              isCentered
              className={Styles['fundraisers-header-type-tooltip']}
              icon={(
                <div
                  className={cx('fundraisers-header-type', { active: activeTime === 'annual' })}
                >
                  Annual
                </div>
              )}
            >
              Coming Soon
            </Tooltip>
          </div>
        </div>
        <div className={cx('fundraisers-header-period', { active: showPeriodChange })}>
          <FontAwesomeIcon
            icon={showPeriodChange ? faCheckSquare : faSquare}
            onClick={() => setShowPeriodChange(!showPeriodChange)}
          />
          <div className={Styles['fundraisers-header-period-text']}>
            Show Period Change
          </div>
          <Tooltip
            newIcon
            hasBigScreenDesign
            isCentered
            dynamicPosition
            contentStyle={{ left: 0 }}
            className={Styles['fundraisers-header-period-tooltip']}
          >
            Toggle whether to show period-to-period changes.
          </Tooltip>
        </div>
        <div className={Styles['fundraisers-header-currency']}>
          {activeType !== 'count' && (
            <>
              <div className={Styles['fundraisers-header-currency-title']}>Currency:</div>
              <div className={Styles['fundraisers-header-currency-value']}>GBP</div>
            </>
          )}
        </div>
      </div>
      <div className={cx('fundraisers-body', { soon: isCommingSoon || !hasInsightsAccess })}>
        <div className={Styles['fundraisers-body-items']}>
          {currentData.loading ? (
            <Preloader loading />
          ) : (
            <>
              {isCommingSoon && (
                <div className={Styles['fundraisers-body-soon']}>
                  <div className={Styles['fundraisers-body-soon-msg']}>Coming Soon</div>
                </div>
              )}
              {width <= 767 ? (
                <EquityFundraisersMobile
                  currentData={currentData.data}
                  activeType={activeType}
                  activeTime={activeTime}
                  showPeriodChange={showPeriodChange}
                  isCommingSoon={isCommingSoon}
                  hasInsightsAccess={hasInsightsAccess}
                />
              ) : (
                <Scrollable
                  wrapperClassName={Styles['fundraisers-body-slider']}
                  arrowClass={Styles['fundraisers-body-slider-arrow']}
                  arrowStyle="compact-centered"
                >
                  {currentData.data.map((obj, i) => (
                    <EquityItem
                      key={i}
                      activeType={activeType}
                      activeTime={activeTime}
                      showPeriodChange={showPeriodChange}
                      index={i}
                      currentItem={obj}
                      isOdd={(i + 1) % 2 === 1}
                      isYtd={i === (currentData.data.length - 1)}
                    />
                  ))}
                </Scrollable>
              )}
            </>
          )}
        </div>
      </div>
      {!currentData.loading && !hasInsightsAccess && (
        <div className={Styles['fundraisers-upgrade']}>
          <div className={Styles['fundraisers-upgrade-message']}>
            <Link to="/pricing">
              Upgrade to UK Insights
            </Link>
            {' '}
            to use this feature.
          </div>
        </div>
      )}
    </div>
  );
};

export default EquityFundraisers;
